import React, { useEffect, useState, useMemo, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { Card, TextField, Button, Autocomplete } from '@totalsoft/rocket-ui';
import { Grid, FormControlLabel, Switch, Checkbox, Tooltip, Link, Paper, Typography } from '@mui/material';
import { emptyArray, emptyString } from 'constants/emptyConst';
import { get, sequence, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { USAGE_TYPE_AND_CATEGORY } from '../../../queries';
import { getErrors, isValid, required, shape } from '@totalsoft/pure-validations';
import { useTranslation } from 'react-i18next';
import { periodicities } from 'constants/common';
import { isDirty } from '@totalsoft/change-tracking';
import { RightsContext } from 'providers/RightsContext';
import { useMutation, useReactiveVar, useSubscription } from '@apollo/client';
import { SAVE_INSURANCE_SIMULATION } from '../../../mutations';
import { useToast, useActivityIndicatorContext } from 'utils/hooks';
import { useServerErrorHandling } from 'utils/hooks/useServerErrorHandling';
import useInsuranceParams from '../../../useInsuranceParams';
import {
  cascoOutdateSimulationVar,
  cascoQuotationsProcessingVar,
  cascoSelectedQuotationVar,
  cascoSelectionInProgressVar
} from '../../../variables';
import { useValidation } from '@totalsoft/pure-validations-react';
import { env } from 'utils/env';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { INSURANCE_QUOTATION_SELECTED, INSURANCE_SIMULATION_SAVED } from '../../../subscriptions';
import { INSURANCE_QUOTATION_SELECTED_EVENT } from '../../../eventTypes';
import { Prompt } from 'react-router-dom';
import { GlobalRightsContext } from 'providers/GlobalRightsContext';

const validator = shape({
  usageTypeCode: required,
  periodicity: required,
  categoryCode: required
});

const CascoInsuranceGeneralData = ({ data, documentDetail, simulation, saving, setSaving, isReadOnly, isOwnInsurance }) => {
  const { t } = useTranslation();
  const { onBeginCall, onEndCall } = useActivityIndicatorContext();
  const addToast = useToast();

  const { isImage } = useContext(RightsContext);
  const { menuInsurance: hasRightToOpenInsurance } = useContext(GlobalRightsContext);
  const { offerId, offerSiteId } = useInsuranceParams();
  const [validation, validate] = useValidation(validator);
  const outdatedSimulation = useReactiveVar(cascoOutdateSimulationVar);

  const [initialPackages, setInitialPackages] = useState(emptyArray);
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens();

  // #region Memos

  const defaultPeriodicicity = useMemo(() => periodicities && periodicities.find(x => x.implicit)?.value, []);
  const leasingSimulation = useMemo(() => {
    // eslint-disable-next-line no-unused-vars
    const { __typename, ...rest } = data?.leasingSimulationByOffer || {};
    return rest;
  }, [data]);
  const insuranceSimulation = useMemo(() => {
    // eslint-disable-next-line no-unused-vars
    const { __typename, ...rest } = data?.insuranceSimulation || {};
    return rest;
  }, [data]);
  const insuranceTypeCode = useMemo(() => lens.insuranceTypeCode |> get, [lens]);
  const itemTypeCode = useMemo(() => lens.itemTypeCode |> get, [lens]);
  const isLensDirty = useMemo(() => isDirty(dirtyInfo), [dirtyInfo]);
  const shouldShowFinancedInsuranceInfo = false;
  const insuranceOfferURL = useMemo(() => {
    if ((lens?.externalInsuranceDocumentId |> get) !== null && (lens?.externalInsuranceDocumentNumber |> get) !== null) {
      return `${env.REACT_APP_LSNG_INSURANCE_URL}/insurance-documents/${lens.externalInsuranceDocumentId |> get}`;
    }
  }, [lens]);

  // #endregion

  // #region Safety Packages

  const { data: usageTypeAndCategory } = useQueryWithErrorHandling(USAGE_TYPE_AND_CATEGORY, {
    variables: {
      insuranceTypeCode,
      itemTypeCode
    },
    skip: !insuranceTypeCode || !itemTypeCode,
    // TODO implementare stergere din cache Apollo; nu doar acest query e afectat, din cate observ atunci cand modific date din cardul
    // general direct in baza de date, si fac reset de cache, tot datele vechi se afiseaza cand revin in pagina. Se va posta task separat.
    // Intre timp pun flag-ul de mai jos (default-ul 'cache-first' ia doar din cache daca gaseste):
    fetchPolicy: 'cache-and-network'
  });

  const safetyPackages = useMemo(() => {
    if (!usageTypeAndCategory?.safetyPackages) return [];
    const assetSafetyPackages = lens.assetSafetyPackages |> sequence;
    return usageTypeAndCategory.safetyPackages.map(safetyPackage => ({
      ...safetyPackage,
      selected: assetSafetyPackages.some(x => (x.code |> get) === safetyPackage.code), // indicates whether a safety package is selected visually in the interface
      initial: initialPackages.includes(safetyPackage.code) // indicates whether a safety package was selected before saving changes
    }));
  }, [lens, usageTypeAndCategory, initialPackages]);

  const toggleSafetyPackage = useCallback(
    code => {
      const assetSafetyPackages = lens.assetSafetyPackages |> get;
      if (assetSafetyPackages.some(x => x.code === code))
        (lens.assetSafetyPackages |> set)(assetSafetyPackages.filter(x => x.code !== code));
      else
        (lens.assetSafetyPackages |> set)([
          ...assetSafetyPackages,
          {
            documentDetailId: documentDetail.documentDetailId,
            siteId: documentDetail.siteId,
            code
          }
        ]);
    },
    [lens, documentDetail]
  );

  // #endregion

  // #region Save

  const { storeKey } = useServerErrorHandling({
    effect: _ => {
      setSaving(false);
      onEndCall();
    }
  });

  const [saveInsuranceSimulation] = useMutation(SAVE_INSURANCE_SIMULATION, {
    context: {
      headers: { offerId, offerSiteId }
    },
    onError: err => {
      addToast(err.message, 'error');
      onEndCall();
      setSaving(false);
    },
    onCompleted: data => {
      const { correlationId } = data.saveInsuranceSimulation;
      storeKey({ correlationId });
    }
  });

  const handleSaveInsuranceSimulation = useCallback(() => {
    const value = lens |> get;
    if (!validate(value)) return;

    const assetSafetyPackageCodes = value.assetSafetyPackages.map(item => {
      return item.code;
    });

    setSaving(true);
    onBeginCall();
    cascoQuotationsProcessingVar(true);

    saveInsuranceSimulation({
      variables: {
        insuranceSimulation: {
          id: simulation.id,
          documentDetailId: documentDetail.documentDetailId,
          documentDetailSiteId: documentDetail.siteId,
          assetInformationId: documentDetail.assetInformationId,
          offerId,
          offerSiteId,
          insuranceTypeCode: simulation.insuranceTypeCode,
          usageTypeCode: value.usageTypeCode,
          categoryCode: value.categoryCode,
          financedInsurance: value.financedInsurance,
          financedInsurancePeriod: value.financedInsurancePeriod === emptyString ? null : value.financedInsurancePeriod,
          periodicity: value.periodicity,
          assetSafetyPackageCodes,
          allowRtiOption: value.allowRtiOption
        }
      }
    });
  }, [
    documentDetail.assetInformationId,
    documentDetail.documentDetailId,
    documentDetail.siteId,
    lens,
    offerId,
    offerSiteId,
    onBeginCall,
    saveInsuranceSimulation,
    setSaving,
    simulation.id,
    simulation.insuranceTypeCode,
    validate
  ]);

  // #endregion

  // #region Side Effects

  useEffect(() => {
    if (isLensDirty) return;
    setInitialPackages((lens.assetSafetyPackages |> get)?.map(x => x.code) || []);
  }, [isLensDirty, lens]);

  useEffect(() => {
    if (!insuranceSimulation || !leasingSimulation || isLensDirty || outdatedSimulation) return;

    resetLens({
      ...insuranceSimulation,
      totalAmountWithVAT: insuranceSimulation?.insuredAmount,
      itemTypeCode: documentDetail.item.typeCode,
      periodicity: insuranceSimulation?.periodicity || defaultPeriodicicity,
      duration: leasingSimulation?.duration,
      financedInsurance: Boolean(insuranceSimulation?.financedInsurance),
      insuredName: leasingSimulation?.insured?.name
    });
  }, [defaultPeriodicicity, documentDetail, insuranceSimulation, isLensDirty, outdatedSimulation, leasingSimulation, resetLens]);

  // #endregion

  // #region Subscriptions

  useSubscription(INSURANCE_SIMULATION_SAVED, {
    variables: { insuranceSimulationId: simulation.id },
    onSubscriptionData: _ => {
      cascoOutdateSimulationVar(true);
      resetLens();
    }
  });

  useGlobalSubscription(INSURANCE_QUOTATION_SELECTED, {
    variables: { offerId, offerSiteId, insuranceSimulationId: simulation.id },
    onSubscriptionData: ({
      subscriptionData: {
        data: { insuranceQuotationSelected }
      }
    }) => {
      if (!insuranceQuotationSelected) return;

      const { selectedInsuranceQuotationId, selectedQuotation } = insuranceQuotationSelected;
      resetLens(prev => ({
        ...prev,
        selectedInsuranceQuotationId
      }));
      cascoSelectedQuotationVar({
        id: selectedInsuranceQuotationId,
        name: selectedQuotation?.cascoQuotation?.conventionInfo?.conventionName
      });

      addToast(t('Notifications.' + INSURANCE_QUOTATION_SELECTED_EVENT), 'info');
      onEndCall();
      cascoSelectionInProgressVar(false);
    }
  });

  // #endregion

  const periodicityOptions = useMemo(
    () =>
      periodicities.map(option => ({
        ...option,
        code: t('Offer.Insurance.' + option.code)
      })),
    [t]
  );

  const periodicityValue = useMemo(() => {
    const periodicity = lens.periodicity |> get;
    if (!periodicity) return '';
    const periodicityOption = periodicityOptions.find(p => p.value === periodicity);
    if (periodicityOption) return periodicityOption;
    if (periodicity > 12) return { value: 12, code: 12 };
    return { value: periodicity, code: periodicity };
  }, [lens, periodicityOptions]);

  return (
    <>
      <Prompt when={isLensDirty} message={() => t('SaveChanges', { card: simulation.insuranceTypeName })} />
      <Card title={t('Offer.Insurance.GeneralData')}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={3}>
            <TextField
              id={'cascoInsurance_insuredAmount'}
              label={t('Offer.Insurance.InsuredAmount')}
              fullWidth
              disabled
              isNumeric
              value={(lens.totalAmountWithVAT |> get) || emptyString}
              inputProps={{ decimalScale: 2, thousandSeparator: true }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <TextField
              id={'cascoInsurance_currency'}
              fullWidth
              label={t('Offer.Insurance.Currency')}
              disabled
              value={(lens.currencyCode |> get) || emptyString}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <TextField
              id={'cascoInsurance_duration'}
              fullWidth
              disabled
              isNumeric
              inputProps={{
                decimalScale: 0,
                thousandSeparator: false
              }}
              label={t('Offer.Insurance.FinancingPeriod')}
              value={(lens.duration |> get) || emptyString}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Autocomplete
              id={'cascoInsurance_paymentPeriodicity'}
              fullWidth
              isClearable
              label={t('Offer.Insurance.PaymentPeriodicity')}
              value={periodicityValue}
              onChange={value => (lens.periodicity |> set)(value.value)}
              valueKey='value'
              labelKey='code'
              options={periodicityOptions}
              error={!isValid(validation.periodicity)}
              helperText={getErrors(validation.periodicity)}
              disabled={(lens.financedInsurance |> get) || isReadOnly()}
            />
          </Grid>
          <Grid item xs={12} lg={5}>
            <Autocomplete
              id={'cascoInsurance_usageType'}
              fullWidth
              isClearable={true}
              label={t('Offer.Insurance.UsageType')}
              simpleValue
              value={lens.usageTypeCode |> get || emptyString}
              onChange={usage => (lens.usageTypeCode |> set)(usage)}
              isSearchable={true}
              valueKey='code'
              labelKey='name'
              options={usageTypeAndCategory?.usageTypes}
              error={!isValid(validation.usageTypeCode)}
              helperText={getErrors(validation.usageTypeCode)}
              disabled={isReadOnly()}
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <Autocomplete
              id={'cascoInsurance_vehicleCategory'}
              fullWidth
              isClearable={true}
              label={t('Offer.Insurance.VehicleCategory')}
              simpleValue
              value={(lens.categoryCode |> get) || emptyString}
              onChange={value => (lens.categoryCode |> set)(value)}
              isSearchable={true}
              valueKey='code'
              labelKey='name'
              options={usageTypeAndCategory?.assetCategories || emptyArray}
              error={!isValid(validation.categoryCode)}
              helperText={getErrors(validation.categoryCode)}
              disabled={isReadOnly()}
            />
          </Grid>
          {shouldShowFinancedInsuranceInfo && (
            <Grid item xs={12} md={6} lg={3}>
              <FormControlLabel
                disabled
                control={
                  <Switch
                    id={'offer_insurance_financedInsurance_casco'}
                    color='primary'
                    disabled
                    checked={(lens.financedInsurance |> get) || false}
                  />
                }
                label={t('Offer.Insurance.FinancedInsurance')}
              />
            </Grid>
          )}
          {shouldShowFinancedInsuranceInfo && (
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                id={'cascoInsurance_financedPeriod'}
                fullWidth
                isNumeric
                inputProps={{
                  decimalScale: 0,
                  thousandSeparator: false
                }}
                value={(lens.financedInsurancePeriod |> get) || emptyString}
                label={t('Offer.Insurance.FinancedPeriod')}
                disabled
              />
            </Grid>
          )}
          <Grid item xs={12} md={6} lg={3}>
            <TextField fullWidth label={t('Offer.Insurance.Insured')} value={(lens.insuredName |> get) || emptyString} disabled />
          </Grid>
          {safetyPackages.length > 0 && (
            <Grid item xs={12}>
              <Typography>{t('Offer.Insurance.SafetyPackages')}</Typography>
            </Grid>
          )}
          <Grid item container xs={12}>
            {safetyPackages
              .filter(
                safetyPackage => !safetyPackage.inactive || safetyPackage.selected || (!safetyPackage.selected && safetyPackage.initial)
              )
              .map(safetyPackage => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={safetyPackage.id}>
                  <FormControlLabel
                    label={
                      <div>
                        <span>{safetyPackage.name}</span>
                        {safetyPackage.inactive && (
                          <Tooltip title={t('Offer.Insurance.InactiveSafetyPackage')} placement={'top'}>
                            <span>{'*'}</span>
                          </Tooltip>
                        )}
                      </div>
                    }
                    control={
                      <Checkbox
                        checked={safetyPackage.selected}
                        disabled={isReadOnly()}
                        onChange={() => toggleSafetyPackage(safetyPackage.code)}
                      />
                    }
                  />
                </Grid>
              ))}
          </Grid>
          <Grid item xs={12} container alignItems='center' justifyContent='flex-end'>
            {insuranceOfferURL && !isImage && !isOwnInsurance && hasRightToOpenInsurance ? (
              <Grid item xs={3} container justifyContent='flex-start'>
                <Paper elevation={0}>
                  <Link href={insuranceOfferURL} underline='hover' color='blue' target='_blank' rel='noopener noreferrer'>
                    {t('Offer.Insurance.InsuranceOfferNumber', {
                      insuranceOffer: lens.externalInsuranceDocumentNumber |> get
                    })}
                  </Link>
                </Paper>
              </Grid>
            ) : (
              <> </>
            )}
            {!isImage && (
              <Grid item xs={9} container justifyContent='flex-end'>
                <Button
                  id={'cascoInsurance_processInsurance'}
                  color={isReadOnly() ? 'defaultNoBackground' : 'primary'}
                  onClick={handleSaveInsuranceSimulation}
                  disabled={isReadOnly()}
                >
                  {saving ? t('Saving') : t('Offer.Insurance.ProcessInsurances')}
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Card>
    </>
  );
};

CascoInsuranceGeneralData.propTypes = {
  data: PropTypes.object,
  isReadOnly: PropTypes.func.isRequired,
  documentDetail: PropTypes.object,
  simulation: PropTypes.object,
  saving: PropTypes.bool.isRequired,
  setSaving: PropTypes.func.isRequired,
  isOwnInsurance: PropTypes.bool.isRequired
};

export default CascoInsuranceGeneralData;
